import React, { Component } from 'react';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography/Typography';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import IconButton from '@material-ui/core/IconButton';
import BackArrow from '@material-ui/icons/ArrowBackIos';
import AppBar from '@material-ui/core/AppBar/AppBar';
import Toolbar from '@material-ui/core/Toolbar/Toolbar';
import Snackbar from '@material-ui/core/Snackbar';
import PropTypes from 'prop-types';

import './SendPage.css';
import SnackbarContentWrapper from '../../Components/SnackbarContentWrapper/SnackbarContentWrapper';
import ConfirmationDialog from '../../Components/ConfirmationDialog/ConfirmationDialog';
import { formatMessage } from '../../utilities/utils';
import { db, getUser, redirectToRoot } from '../../utilities/firebase-config';
import styles from './SendPage.style';

const defaultConfirmationDialog = {
  display: false,
  message: '',
  messages: [],
  negativeAction: () => {},
  negativeText: '',
  positiveAction: () => {},
  positiveText: '',
  title: '',
};

const initialState = {
  additionalNumbers: '',
  confirmationDialog: defaultConfirmationDialog,
  extraHail: '',
  extraHazard: '',
  extraText: '',
  extraWind: '',
  snackbar: {
    message: '',
    open: false,
    variant: 'success',
  },
  submitted: false,
  submitting: false,
  uid: null,
  uselist: false,
  wxMessagetype: '',
  wxRepeater: '',
  wxTimeHour: '',
  wxTimeMinutes: '',
};

class SendPage extends Component {
  state = initialState;

  async componentDidMount() {
    const { history } = this.props;
    const user = await getUser();

    if (!user) {
      redirectToRoot(history);
    }

    if (!user || !user.profile || (user.profile.permissions !== 'Administrator' && user.profile.permissions !== 'Sender')) {
      redirectToRoot(history);
    }

    this.setState({
      uid: user.uid,
    });

    // Ensure intial state is updated with uid as it is used again later
    // if another page is sent without leaving the view.
    initialState.uid = user.uid;
  }

  goBack = () => {
    const { history: { goBack } } = this.props;
    goBack();
  };

  toggleUselist = () => {
    const { uselist } = this.state;

    this.setState({
      uselist: !uselist,
    });
  };

  changeField = (event) => {
    this.setState({
      [event.target.name]: event.target.value,
    });
  };

  closeSnackbar = () => {
    const { snackbar } = this.state;

    this.setState({
      snackbar: {
        ...snackbar,
        open: false,
      },
    });
  };

  submit = (event) => {
    const requiredActions = [];
    event.preventDefault();

    this.setState({
      submitted: true,
      submitting: true,
    });

    window.scrollTo(0, 0);

    // eslint-disable-next-line react/no-access-state-in-setstate
    const pageRequest = { ...this.state };
    const { uid } = this.state;

    pageRequest.uselist = pageRequest.uselist ? 'on' : 'off';
    pageRequest.wxMessagetype = pageRequest.wxMessagetype ? pageRequest.wxMessagetype : null;
    pageRequest.wxTimeHour = pageRequest.wxTimeHour === '' ? '' : Number(pageRequest.wxTimeHour);
    pageRequest.wxTimeMinutes = pageRequest.wxTimeMinutes === '' ? '' : Number(pageRequest.wxTimeMinutes);
    pageRequest.uid = uid;
    pageRequest.ts = firebase.database.ServerValue.TIMESTAMP;
    // If it's a PSA, there should be no repeater
    pageRequest.wxRepeater = pageRequest.wxMessagetype === 'PSA / Info' ? '' : pageRequest.wxRepeater;
    // If it's training net and no net is already selected,
    // force it to an empty string to fail validation
    pageRequest.wxRepeater = pageRequest.wxMessagetype === 'Training Net' && pageRequest.wxRepeater === 'No net' ? '' : pageRequest.wxRepeater;

    // Build up array of any issues the user must solve
    if (pageRequest.wxMessagetype === 'Training Net' && pageRequest.wxRepeater === '') {
      requiredActions.push('When sending a Training Net text, a network must be selected in "Net Required"');
    }

    if (!pageRequest.wxMessagetype) {
      requiredActions.push('Please select a Page Type');
    }

    if (!pageRequest.wxRepeater && pageRequest.wxMessagetype !== 'PSA / Info') {
      requiredActions.push('Please select a network in "Net Required"');
    }

    if (pageRequest.uselist === 'off' && !pageRequest.additionalNumbers) {
      requiredActions.push('Please select "Send Page To Entire Group" or add phone numbers to "Additional Phone Numbers"');
    }

    // If there are any problems, alert user and prevent submitting text
    if (requiredActions.length) {
      this.setState({
        confirmationDialog: {
          ...defaultConfirmationDialog,
          display: true,
          messages: requiredActions,
          negativeAction: this.closeConfirmationDialog,
          negativeText: 'Okay',
          title: 'Action Required',
        },
        submitting: false,
      });

      return;
    }

    const formattedMessage = formatMessage(pageRequest);

    this.setState({
      confirmationDialog: {
        ...defaultConfirmationDialog,
        display: true,
        message: this.generatePageConfirmation(
          formattedMessage, pageRequest.uselist, pageRequest.additionalNumbers,
        ),
        negativeAction: this.closeConfirmationDialog,
        negativeText: 'No',
        positiveAction: this.sendPage.bind(null, pageRequest),
        positiveText: 'Yes',
        title: 'Confirm',
      },
    });
  };

  reset = () => {
    window.scrollTo(0, 0);
    this.setState(initialState);
  };

  closeConfirmationDialog = () => {
    this.setState({
      confirmationDialog: defaultConfirmationDialog,
      submitting: false,
    });

    setTimeout(() => {
      window.scrollTo(0, 0);
    });
  };

  sendPage = (pageRequest) => {
    const newPageRequest = {
      ...pageRequest,
    };

    delete newPageRequest.submitted;
    delete newPageRequest.submitting;
    delete newPageRequest.submitting;
    delete newPageRequest.confirmationDialog;
    delete newPageRequest.snackbar;

    this.setState({
      confirmationDialog: {
        ...defaultConfirmationDialog,
        display: false,
      },
    });

    db.ref('pageRequests').push(newPageRequest)
      .then(() => {
        this.setState({
          ...initialState,
          snackbar: {
            message: 'Text Requested',
            open: true,
            variant: 'success',
          },
        });
      })
      .catch(() => {
        this.setState({
          snackbar: {
            message: 'Failed to Send Text Request',
            open: true,
            variant: 'error',
          },
        });
      })
      .finally(() => {
        this.setState({
          submitting: false,
        });
      });
  };

  generatePageConfirmation = (message, uselist, hasAdditionalNumbers) => (
    <span style={ { display: 'block' } }>
      <Typography gutterBottom variant="h6" component="span">
        Send This Message:
      </Typography>
      <span style={ { display: 'block', marginBottom: 8 } }>
        { message }
      </span>
      <Typography gutterBottom variant="h6" component="span">
        To:
      </Typography>
      <span style={ { display: 'block' } }>
        { uselist === 'on'
        && <span>Entire distribution list</span>
        }

        { uselist === 'on' && hasAdditionalNumbers
        && <span> and </span>
        }

        { hasAdditionalNumbers
        && <span>All additional numbers</span>
        }

      </span>
    </span>
  );

  render() {
    const { classes } = this.props;
    const {
      additionalNumbers,
      confirmationDialog,
      extraHail,
      extraHazard,
      extraText,
      extraWind,
      snackbar,
      submitted,
      submitting,
      uselist,
      wxMessagetype,
      wxRepeater,
      wxTimeHour,
      wxTimeMinutes,
    } = this.state;

    return (
      <div className="SendPage container">
        <AppBar position="fixed" color="primary">
          <Toolbar>
            <IconButton className={ classes.menuButton } color="inherit" aria-label="Menu" onClick={ this.goBack }>
              <BackArrow />
            </IconButton>

            <Typography variant="h6" color="inherit">
              Send Page
            </Typography>
          </Toolbar>
        </AppBar>

        <ConfirmationDialog
          open={ confirmationDialog.display }
          negativeAction={ confirmationDialog.negativeAction }
          negativeText={ confirmationDialog.negativeText }
          positiveAction={ confirmationDialog.positiveAction }
          positiveText={ confirmationDialog.positiveText }
          title={ confirmationDialog.title }
          message={ confirmationDialog.message }
          messages={ confirmationDialog.messages }
        />
        <form onSubmit={ this.submit } name="send-page">
          <Typography gutterBottom variant="h5" component="h2">
            General Page Info
          </Typography>

          <FormGroup row>
            <FormControlLabel
              control={ (
                <Checkbox
                  checked={ uselist }
                  onChange={ this.toggleUselist }
                  value="true"
                  color="primary"
                  name="uselist"
                />
              ) }
              label="Send Page To Entire Group"
            />
          </FormGroup>

          <FormControl className={ classes.formControl }>
            <InputLabel htmlFor="wxMessagetype">Page Type</InputLabel>
            <Select
              value={ wxMessagetype }
              onChange={ this.changeField }
              inputProps={ {
                id: 'wxMessagetype',
                name: 'wxMessagetype',
              } }
              className={ classes.selectEmpty }
            >
              <MenuItem value="SVR THUND WATCH">SVR THUND WATCH</MenuItem>
              <MenuItem value="SVR THUND WARN">SVR THUND WARN</MenuItem>
              <MenuItem value="TORN WATCH">TORN WATCH</MenuItem>
              <MenuItem value="TORN WARN">TORN WARN</MenuItem>
              <MenuItem value="FLASH FLOOD WARN">FLASH FLOOD WARN</MenuItem>
              <MenuItem value="Training Net">Training Net</MenuItem>
              <MenuItem value="Net Activation">Net Activation</MenuItem>
              <MenuItem value="PSA / Info">PSA / Info</MenuItem>
            </Select>

            { submitted && wxMessagetype === ''
            && <div className={ classes.error }>Please select a Page Type</div>
            }
          </FormControl>

          { wxMessagetype !== 'PSA / Info' && (
            <FormControl className={ classes.formControl }>
              <InputLabel htmlFor="wxRepeater">Net Required</InputLabel>
              <Select
                value={ wxRepeater }
                onChange={ this.changeField }
                inputProps={ {
                  id: 'wxRepeaterwxRepeater',
                  name: 'wxRepeater',
                } }
              >
                { wxMessagetype !== 'Training Net' && (
                  <MenuItem value="No net">No net</MenuItem>
                )}
                <MenuItem value="147.18">147.18</MenuItem>
                <MenuItem value="146.74">146.74</MenuItem>
                <MenuItem value="444.25">444.25</MenuItem>
                <MenuItem value="145.35 (-) 100.0">145.35 (-) 100.0</MenuItem>
              </Select>
            </FormControl>
          )
          }

          <Grid container spacing={ 24 }>
            <Grid item xs={ 6 }>
              <FormControl className={ classes.formControl }>
                <InputLabel htmlFor="wxTimeHour">Hour</InputLabel>
                <Select
                  value={ wxTimeHour }
                  onChange={ this.changeField }
                  inputProps={ {
                    id: 'wxTimeHour',
                    name: 'wxTimeHour',
                  } }
                  className={ classes.selectEmpty }
                >
                  <MenuItem value="" />
                  <MenuItem value="0">00</MenuItem>
                  <MenuItem value="1">01</MenuItem>
                  <MenuItem value="2">02</MenuItem>
                  <MenuItem value="3">03</MenuItem>
                  <MenuItem value="4">04</MenuItem>
                  <MenuItem value="5">05</MenuItem>
                  <MenuItem value="6">06</MenuItem>
                  <MenuItem value="7">07</MenuItem>
                  <MenuItem value="8">08</MenuItem>
                  <MenuItem value="9">09</MenuItem>
                  <MenuItem value="10">10</MenuItem>
                  <MenuItem value="11">11</MenuItem>
                  <MenuItem value="12">12</MenuItem>
                  <MenuItem value="13">13</MenuItem>
                  <MenuItem value="14">14</MenuItem>
                  <MenuItem value="15">15</MenuItem>
                  <MenuItem value="16">16</MenuItem>
                  <MenuItem value="17">17</MenuItem>
                  <MenuItem value="18">18</MenuItem>
                  <MenuItem value="19">19</MenuItem>
                  <MenuItem value="20">20</MenuItem>
                  <MenuItem value="21">21</MenuItem>
                  <MenuItem value="22">22</MenuItem>
                  <MenuItem value="23">23</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={ 6 }>
              <FormControl className={ classes.formControl }>
                <InputLabel htmlFor="wxTimeMinutes">Minutes</InputLabel>
                <Select
                  value={ wxTimeMinutes }
                  onChange={ this.changeField }
                  inputProps={ {
                    id: 'wxTimeMinutes',
                    name: 'wxTimeMinutes',
                  } }
                  className={ classes.selectEmpty }
                >
                  <MenuItem value="" />
                  <MenuItem value="00">00</MenuItem>
                  <MenuItem value="05">05</MenuItem>
                  <MenuItem value="10">10</MenuItem>
                  <MenuItem value="15">15</MenuItem>
                  <MenuItem value="20">20</MenuItem>
                  <MenuItem value="25">25</MenuItem>
                  <MenuItem value="30">30</MenuItem>
                  <MenuItem value="35">35</MenuItem>
                  <MenuItem value="40">40</MenuItem>
                  <MenuItem value="45">45</MenuItem>
                  <MenuItem value="50">50</MenuItem>
                  <MenuItem value="55">55</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography gutterBottom variant="h5" component="h2" className={ classes.header }>
            Additional Text To Be Sent
          </Typography>

          <FormControl className={ classes.formControl }>
            <InputLabel htmlFor="extraWind">Wind Information</InputLabel>
            <Select
              value={ extraWind }
              onChange={ this.changeField }
              inputProps={ {
                id: 'extraWind',
                name: 'extraWind',
              } }
              className={ classes.selectEmpty }
            >
              <MenuItem value="" />
              <MenuItem value="Wind 30 mph or greater">Wind 30 mph or greater</MenuItem>
              <MenuItem value="Wind 35 mph or greater">Wind 35 mph or greater</MenuItem>
              <MenuItem value="Wind 40 mph or greater">Wind 40 mph or greater</MenuItem>
              <MenuItem value="Wind 45 mph or greater">Wind 45 mph or greater</MenuItem>
              <MenuItem value="Wind 50 mph or greater">Wind 50 mph or greater</MenuItem>
              <MenuItem value="Wind 55 mph or greater">Wind 55 mph or greater</MenuItem>
              <MenuItem value="Wind 60 mph or greater">Wind 60 mph or greater</MenuItem>
              <MenuItem value="Wind 65 mph or greater">Wind 65 mph or greater</MenuItem>
              <MenuItem value="Wind 70 mph or greater">Wind 70 mph or greater</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={ classes.formControl }>
            <InputLabel htmlFor="extraHail">Hail Information</InputLabel>
            <Select
              value={ extraHail }
              onChange={ this.changeField }
              inputProps={ {
                id: 'extraHail',
                name: 'extraHail',
              } }
              className={ classes.selectEmpty }
            >
              <MenuItem value="" />
              <MenuItem value="Hail 0.25 or larger">Hail 0.25&quot; or larger</MenuItem>
              <MenuItem value="Hail 0.50 or larger">Hail 0.50&quot; or larger</MenuItem>
              <MenuItem value="Hail 0.625 or larger">Hail 0.625&quot; or larger</MenuItem>
              <MenuItem value="Hail 0.75 or larger">Hail 0.75&quot; or larger</MenuItem>
              <MenuItem value="Hail 1.0 or larger">Hail 1.0&quot; or larger</MenuItem>
              <MenuItem value="Hail 1.25 or larger">Hail 1.25&quot; or larger</MenuItem>
              <MenuItem value="Hail 1.50 or larger">Hail 1.50&quot; or larger</MenuItem>
              <MenuItem value="Hail 1.75 or larger">Hail 1.75&quot; or larger</MenuItem>
              <MenuItem value="Hail 2.0 or larger">Hail 2.0&quot; or larger</MenuItem>
              <MenuItem value="Hail 2.25 or larger">Hail 2.25&quot; or larger</MenuItem>
              <MenuItem value="Hail 2.50 or larger">Hail 2.50&quot; or larger</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={ classes.formControl }>
            <InputLabel htmlFor="extraHazard">Hazards Information</InputLabel>
            <Select
              value={ extraHazard }
              onChange={ this.changeField }
              inputProps={ {
                id: 'extraHazard',
                name: 'extraHazard',
              } }
              className={ classes.selectEmpty }
            >
              <MenuItem value="" />
              <MenuItem value="No history of tornadoes">No history of tornadoes</MenuItem>
              <MenuItem value="History of tornadoes">History of tornadoes</MenuItem>
            </Select>
          </FormControl>

          <TextField
            className={ classes.textArea }
            id="extraText"
            label="Additional Info"
            placeholder="Enter additional info you want included in the text"
            name="extraText"
            fullWidth
            multiline
            rows={ 2 }
            margin="normal"
            variant="outlined"
            value={ extraText }
            onChange={ this.changeField }
            InputLabelProps={ {
              shrink: true,
            } }
          />

          <TextField
            className={ classes.textArea }
            id="additionalNumbers"
            label="Additional Phone Numbers"
            placeholder="Enter additional phone numbers to receive the text"
            name="additionalNumbers"
            fullWidth
            multiline
            rows={ 2 }
            margin="normal"
            variant="outlined"
            value={ additionalNumbers }
            onChange={ this.changeField }
            InputLabelProps={ {
              shrink: true,
            } }
          />

          <Button
            variant="outlined"
            color="primary"
            className={ classes.button }
            onClick={ this.submit }
            type="submit"
            disabled={ submitting }
          >
            Send Page
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            className={ classes.button }
            onClick={ this.reset }
          >
            Reset
          </Button>
        </form>

        <Snackbar
          anchorOrigin={ {
            horizontal: 'left',
            vertical: 'bottom',
          } }
          open={ snackbar.open }
          autoHideDuration={ 5000 }
          onClose={ this.closeSnackbar }
        >
          <SnackbarContentWrapper
            onClose={ this.closeSnackbar }
            variant={ snackbar.variant }
            message={ snackbar.message }
          />
        </Snackbar>

      </div>
    );
  }
}

SendPage.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withStyles(styles)(SendPage);
